/* eslint-disable react-hooks/exhaustive-deps */
// src/hooks/artistEvents/useArtistEventDetailsElement.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ArtistSidebar from '../../components/artist/ArtistSidebar';
import { MemberProfileModal as ArtistMemberProfileModal } from '../../components/artist/chats/ArtistMemberListModal';
import ArtistBubbleView from '../../components/admin/artists/ArtistBubbleView';
import EventListRow from '../../components/common/events/EventListRow';
import EventListSkeleton from '../../components/common/events/EventListSkeleton';
import EventMaterialExplorer from '../../components/common/events/EventMaterialExplorer';
import EventFlyerMedia from '../../components/common/EventFlyerMedia';
import { useToast } from '../../components/common/ToastSystem';
import DetailPageSkeleton from '../../components/common/DetailPageSkeleton';
import { ActionRowsSkeleton } from '../../components/common/ContentSkeletons';
import DsButton from '../../components/common/DsButton';
import ArtistSharedTicketSalesPanel from '../../components/artist/events/ArtistSharedTicketSalesPanel';
import '../../css/artist/events/ArtistEventDetails.css';
import '../../css/common/events/EventListCard.css';
import '../../css/common/events/EventListRow.css';
import { getSocket } from '../../lib/socket';
import { SOCKET_EVENTS } from '../../lib/socketEvents';
import { openExternalUrl } from '../../utils/externalLink';
import { PARTICIPATING_CONTRACT_STATUSES } from '../../utils/contractStatus';
import {
  GENRE_CHIP_FALLBACK_BG,
  GENRE_CHIP_FALLBACK_TEXT,
} from '../../utils/genreUtils';
import { listParts } from '../../api/artists';
import { listGenres } from '../../api/genres';
import {
  getArtistEventGroupEvents,
  getArtistStageSeatingSharedSummary,
  listCommonEventCastingProgress,
  listEventCastingInfo,
} from '../../api/eventDetails';
import {
  getEventDetail,
  getEventTimetableRuntimeState,
  listSharedEventTimetables,
} from '../../api/taskEvents';
import { getVenueDetail } from '../../api/venues';
import {
  getEventPerformances,
  getPerformanceDate,
  getPerformanceName,
  getEventPerformanceStructureRows,
  isMultiPerformanceEvent,
} from '../../utils/eventFormDisplay';
import {
  applyRowAdjustments,
  buildAdjustmentMap,
  buildMemoMap,
  buildTimelineRows,
  normalizeTimetableTargetDate as normalizeTargetDate,
} from '../../utils/timetableTimeline';
import EventVenueDisplay, {
  buildVenueAddress,
  buildVenueMapUrl,
} from '../../components/common/events/EventVenueDisplay';

import { formatDateJP, formatDateJPFromAny, toHHmm, formatDateTimeJST } from '../../utils/datetime';

const API_URL = process.env.REACT_APP_API_URL;
// MinIO 経由の配信パス（末尾スラッシュを除去）
const FLYER_IMAGE_PATH = (process.env.REACT_APP_FLYER_IMAGE_PATH || '/api/images/flyers').replace(
  /\/$/,
  ''
);
const BANNER_IMAGE_PATH = (
  process.env.REACT_APP_EVENT_BANNER_IMAGE_PATH ||
  process.env.REACT_APP_BANNER_IMAGE_PATH ||
  '/api/images/event_banners'
).replace(/\/$/, '');
const ARTIST_PROFILE_IMAGE_PATH = (process.env.REACT_APP_ARTIST_PROFILE_IMAGE_PATH || '/api/images/artist_profiles').replace(
  /\/$/,
  ''
);
const PARTICIPANT_STATUSES = PARTICIPATING_CONTRACT_STATUSES;
const toNum = (v) => (typeof v === 'number' ? v : Number(v));
const EXPECTED_EMPTY_SUPPLEMENTAL_STATUSES = new Set([204, 403, 404]);

const getApiStatusCode = (error) => {
  const status = Number(
    error?.status ??
      error?.response?.status ??
      error?.payload?.status ??
      error?.data?.status
  );
  return Number.isFinite(status) ? status : null;
};

const isExpectedEmptySupplementalError = (error) =>
  EXPECTED_EMPTY_SUPPLEMENTAL_STATUSES.has(getApiStatusCode(error));

const logUnexpectedSupplementalError = (label, error) => {
  if (!isExpectedEmptySupplementalError(error)) {
    console.warn(label, error?.message || error);
  }
};

function parsePartIds(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(toNum).filter(Number.isFinite);
  if (typeof raw === 'string') {
    try {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) return arr.map(toNum).filter(Number.isFinite);
    } catch (_) {
      // fall through
    }
    return raw
      .split(/[,\s]+/)
      .map(toNum)
      .filter(Number.isFinite);
  }
  return [];
}

const appendVariant = (url, variant) => {
  if (!url || !variant) return url || '';
  if (/[?&]variant=/.test(url)) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}variant=${encodeURIComponent(variant)}`;
};

const toTimestamp = (value) => {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const firstNonEmpty = (...vals) => vals.find((v) => typeof v === 'string' && v.trim()) || '';

const buildParticipantProfile = (artist) => {
  if (!artist) return null;
  const name = firstNonEmpty(artist.name, artist.artist_name);
  const nameEn = firstNonEmpty(artist.name_en, artist.artist_name_en, artist.nameEn);
  const partLabel = firstNonEmpty(artist.partLabel, artist.part, artist.mainPartLabel, artist.mainPart);
  return {
    id: artist.artist_id || artist.id || null,
    name,
    nameEn,
    initial: (name || '').trim().charAt(0) || '',
    pic: artist.profile_picture || artist.pic || artist.artist_profile_picture || '',
    part: partLabel,
    isChief: !!artist.is_chief || !!artist.isChief,
    isConcertmaster: !!artist.is_concertmaster || !!artist.isConcertmaster,
    customRoles: Array.isArray(artist.custom_roles)
      ? artist.custom_roles
      : Array.isArray(artist.customRoles)
      ? artist.customRoles
      : [],
    instagram: artist.instagram_url || '',
    twitter: artist.twitter_url || '',
    facebook: artist.facebook_url || '',
    hp: artist.hp_url || artist.homepage_url || '',
    comment: firstNonEmpty(
      artist.profile_one_liner,
      artist.one_liner,
      artist.comment,
      artist.bio,
      artist.notes
    ),
  };
};

const getImageUrl = (filename) =>
  filename ? `${API_URL}${FLYER_IMAGE_PATH}/${encodeURIComponent(filename)}` : null;
const getBannerUrl = (filename) =>
  filename ? `${API_URL}${BANNER_IMAGE_PATH}/${encodeURIComponent(filename)}` : null;

const performanceFlagLabels = {
  single: '単発公演',
  first: '初回公演',
  additional: '追加公演',
};

export default function useArtistEventDetailsElement() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [eventData, setEventData] = useState(null);
  const [flyerUrls, setFlyerUrls] = useState({ front: null, back: null });
  const [bannerUrl, setBannerUrl] = useState(null);
  const [currentFlyerIndex, setCurrentFlyerIndex] = useState(0);
  const [showFlyerPopup, setShowFlyerPopup] = useState(false);
  const [genres, setGenres] = useState([]);
  const [venueLink, setVenueLink] = useState(null);
  const [venueResolvedAddress, setVenueResolvedAddress] = useState('');
  const [venueResolvedName, setVenueResolvedName] = useState('');
  const [profileModalArtist, setProfileModalArtist] = useState(null);
  const [castingInfo, setCastingInfo] = useState([]);
  const [castingProgress, setCastingProgress] = useState([]);
  const [partsList, setPartsList] = useState([]);
  const [participantView, setParticipantView] = useState('list');
  const [timetables, setTimetables] = useState([]);
  const [timetableLoading, setTimetableLoading] = useState(false);
  const [activeTimetableId, setActiveTimetableId] = useState(null);
  const [stageSeatingShared, setStageSeatingShared] = useState({ layouts: [], shares: [] });
  const [stageSeatingSharedLoading, setStageSeatingSharedLoading] = useState(false);
  const [nowTimetableRowId, setNowTimetableRowId] = useState(null);
  const [runtimeData, setRuntimeData] = useState({ adjustments: [], memos: [], announcements: [] });
  const [groupEvents, setGroupEvents] = useState([]);
  const [hasGroupEvents, setHasGroupEvents] = useState(false);
  const [groupEventsLoading, setGroupEventsLoading] = useState(false);
  const [groupFlyerLandscapeMap, setGroupFlyerLandscapeMap] = useState({});
  const highlightTimerRef = useRef(null);
  const { error: toastError } = useToast();
  const adjustmentsMap = useMemo(
    () => buildAdjustmentMap(runtimeData?.adjustments || []),
    [runtimeData?.adjustments],
  );
  const memoMap = useMemo(
    () => buildMemoMap(runtimeData?.memos || []),
    [runtimeData?.memos],
  );
  const canEditSharedStageSeating = useMemo(
    () => stageSeatingShared.layouts.some((layout) => layout.can_edit === true),
    [stageSeatingShared.layouts],
  );

  const fetchTimetableRuntime = useCallback(async (tableId) => {
    if (!eventId || !tableId) {
      setRuntimeData({ adjustments: [], memos: [], announcements: [] });
      return;
    }
    try {
      const res = await getEventTimetableRuntimeState(eventId, tableId);
      setRuntimeData({
        adjustments: Array.isArray(res?.adjustments) ? res.adjustments : [],
        memos: Array.isArray(res?.memos) ? res.memos : [],
        announcements: Array.isArray(res?.announcements) ? res.announcements : [],
      });
    } catch (err) {
      logUnexpectedSupplementalError('Timetable runtime fetch failed:', err);
      setRuntimeData({ adjustments: [], memos: [], announcements: [] });
      if (!isExpectedEmptySupplementalError(err)) {
        toastError('タイムテーブルの実行情報の取得に失敗しました');
      }
    }
  }, [eventId, toastError]);

  useEffect(() => {
    if (!eventId) return;
    fetchEventDetails();
    fetchGroupEvents();
    fetchGenres();
    fetchTimetables();
    fetchStageSeatingShared();
    fetchParticipants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await listParts();
        if (cancelled) return;
        setPartsList(Array.isArray(data) ? data : data?.parts || []);
      } catch (error) {
        console.error('Error fetching parts:', error);
        if (!cancelled) setPartsList([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const clearHighlightTimer = useCallback(() => {
    if (highlightTimerRef.current) {
      clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }
  }, []);

  // タイムテーブル helper 群は下段定義を共有しており、この callback では現在値で再評価する
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const scheduleHighlightTick = useCallback(() => {
    clearHighlightTimer();
    const active = timetables.find((t) => t.id === activeTimetableId) || timetables[0];
    if (!active) {
      setNowTimetableRowId(null);
      return;
    }
    setNowTimetableRowId(findNowRowId(active, adjustmentsMap));

    const nextStart = findNextStartMs(active, adjustmentsMap);
    if (!nextStart) return;

    const now = Date.now();
    const diff = nextStart - now;
    let delay = 10000;
    if (diff <= 0) delay = 500;
    else if (diff < 1000) delay = Math.max(120, diff);
    else if (diff < 5000) delay = 500;
    else if (diff < 15000) delay = 1000;
    else if (diff < 60000) delay = 3000;
    highlightTimerRef.current = setTimeout(scheduleHighlightTick, delay);
  }, [activeTimetableId, timetables, adjustmentsMap, clearHighlightTimer]);

  useEffect(() => {
    scheduleHighlightTick();
    return clearHighlightTimer;
  }, [scheduleHighlightTick, clearHighlightTimer]);

  useEffect(() => {
    if (!activeTimetableId) {
      setRuntimeData({ adjustments: [], memos: [], announcements: [] });
      return;
    }
    fetchTimetableRuntime(activeTimetableId);
  }, [activeTimetableId, fetchTimetableRuntime]);

  useEffect(() => {
    if (!eventId) return;
    const socket = getSocket();
    if (!socket) return;
    if (!socket.connected) {
      try {
        socket.connect();
      } catch (err) {
        // ignore connect errors
      }
    }
    try {
      socket.emit(SOCKET_EVENTS.JOIN_EVENT, eventId);
    } catch (err) {
      // ignore join errors
    }
    const eventName = SOCKET_EVENTS.TIMETABLE_RUNTIME_UPDATE || 'timetable_runtime_update';
    const handleRuntimeUpdate = (payload = {}) => {
      if (payload?.eventId && String(payload.eventId) !== String(eventId)) return;
      const updatedTimetableId = payload?.timetableId ?? payload?.timetable_id ?? null;
      if (updatedTimetableId && activeTimetableId && Number(updatedTimetableId) !== Number(activeTimetableId)) {
        return;
      }
      if (!activeTimetableId) return;
      fetchTimetableRuntime(activeTimetableId);
    };
    socket.on(eventName, handleRuntimeUpdate);
    return () => {
      socket.off(eventName, handleRuntimeUpdate);
      try {
        socket.emit('leave_event', eventId);
      } catch (err) {
        // ignore leave errors
      }
    };
  }, [eventId, activeTimetableId, fetchTimetableRuntime]);

  const fetchEventDetails = async () => {
    try {
      const event = await getEventDetail(eventId);
      const data = { event };
      if (!data.event) throw new Error('event not found');

      // selected_options を配列に正規化
      const options = data.event.selected_options;
      data.event.selected_options = Array.isArray(options)
        ? options
        : (function () {
            try {
              return JSON.parse(options || '[]');
            } catch {
              return [];
            }
          })();

      setEventData(data.event);

      // 追加公演かつフライヤー再利用ならオリジナルイベントのフライヤー情報を使用
      const shouldInherit =
        data.event.performance_flag === 'additional' &&
        data.event.use_existing_flyers &&
        data.event.original_event_uuid;

      if (shouldInherit) {
        await fetchOriginalEventAssets(data.event.original_event_uuid);
      } else {
        setFlyerUrls({
          front: getImageUrl(data.event.flyer_front_url),
          back: getImageUrl(data.event.flyer_back_url),
        });
        setBannerUrl(getBannerUrl(data.event.banner_image_url));
      }

    } catch (error) {
      console.error('Error fetching event details:', error);
      toastError('イベント詳細の取得に失敗しました');
    }
  };

  const fetchGroupEvents = async () => {
    if (!eventId) {
      setGroupEvents([]);
      setHasGroupEvents(false);
      return;
    }
    setGroupEventsLoading(true);
    try {
      const data = await getArtistEventGroupEvents(eventId);
      const hasGroup = data?.has_group === true;
      const list = Array.isArray(data?.events) ? data.events : [];
      setHasGroupEvents(hasGroup && list.length > 0);
      setGroupEvents(hasGroup ? list : []);
      setGroupFlyerLandscapeMap({});
    } catch (err) {
      logUnexpectedSupplementalError('Artist group events fetch failed:', err);
      setHasGroupEvents(false);
      setGroupEvents([]);
    } finally {
      setGroupEventsLoading(false);
    }
  };

  const fetchParticipants = async () => {
    if (!eventId) return;
    try {
      const [ciJson, pgJson] = await Promise.all([
        listEventCastingInfo(eventId),
        listCommonEventCastingProgress(eventId),
      ]);
      setCastingInfo(Array.isArray(ciJson) ? ciJson : []);
      setCastingProgress(Array.isArray(pgJson) ? pgJson : []);
    } catch (error) {
      logUnexpectedSupplementalError('Participants fetch failed:', error);
      setCastingInfo([]);
      setCastingProgress([]);
    }
  };

  // 会場リンク/住所解決（IDのみ）
  useEffect(() => {
    let cancelled = false;
    const venueId = eventData?.venue_id ?? eventData?.venue?.id ?? null;
    const fallbackName = eventData?.venue || eventData?.venue_name || '';
    const buildAddress = (v) => [v?.prefecture, v?.city, v?.address].filter(Boolean).join(' ').trim();

    (async () => {
      if (!venueId) {
        setVenueLink(null);
        setVenueResolvedAddress('');
        setVenueResolvedName('');
        return;
      }
      if (!cancelled) {
        setVenueLink(`/artist/workspaces/event-operations/venues/${venueId}`);
      }
      try {
        const venue = await getVenueDetail(String(venueId));
        if (cancelled) return;
        setVenueResolvedName((venue?.name || fallbackName || '').trim());
        setVenueResolvedAddress(buildAddress(venue));
      } catch {
        if (!cancelled) {
          setVenueResolvedName((fallbackName || '').trim());
          setVenueResolvedAddress('');
        }
      }
    })();

    return () => { cancelled = true; };
  }, [eventData?.venue_id, eventData?.venue, eventData?.venue_name]);

  const venueIdValue = useMemo(() => {
    const raw = eventData?.venue_id ?? eventData?.venue?.id ?? null;
    if (raw === null || raw === undefined || raw === '') return null;
    return String(raw);
  }, [eventData?.venue_id, eventData?.venue]);

  const venueDisplayName = useMemo(() => {
    if (venueIdValue) return (venueResolvedName || eventData?.venue || '').trim();
    return (eventData?.venue || '').trim();
  }, [venueIdValue, venueResolvedName, eventData?.venue]);

  const mapLinkUrl = useMemo(() => {
    return buildVenueMapUrl({
      name: venueDisplayName,
      address: venueResolvedAddress,
      fallback: eventData?.prefecture || '',
    });
  }, [venueDisplayName, venueResolvedAddress, eventData?.prefecture]);

  const multiPerformanceRows = useMemo(
    () => getEventPerformances(eventData),
    [eventData]
  );
  const isMultiPerformanceEventData = isMultiPerformanceEvent(eventData);
  const getPerformanceVenueInfo = useCallback((performance = {}) => {
    const name = (
      performance.venue_name ||
      performance.venue_name_snapshot ||
      performance.venue ||
      ''
    ).trim();
    const address = buildVenueAddress(performance);
    return {
      venueId: performance.venue_id ?? performance.venueId ?? null,
      name,
      address,
      mapUrl: buildVenueMapUrl({
        name,
        address,
        fallback: performance.prefecture || performance.venue_prefecture || '',
      }),
    };
  }, []);

  const openArtistProfile = useCallback((cast) => {
    if (!cast?.artist_id) return;
    setProfileModalArtist({
      name: cast.artist_name || '',
      pic: cast.artist_profile_picture || null,
      initial: (cast.artist_name || '').trim().charAt(0) || '',
    });
  }, []);

  const getDefaultColumnAlign = (id) => {
    if (id === 'startTime' || id === 'duration') return 'center';
    return 'left';
  };

  const DEFAULT_TIMETABLE_COLUMNS = [
    { id: 'startTime', label: '時刻', align: getDefaultColumnAlign('startTime') },
    { id: 'duration', label: '所要時間', align: getDefaultColumnAlign('duration') },
    { id: 'item', label: '進行・曲目', align: getDefaultColumnAlign('item') },
  ];

  const buildPreviewColumns = (data = {}) => {
    const cols = Array.isArray(data.columns) && data.columns.length ? data.columns : DEFAULT_TIMETABLE_COLUMNS;
    return cols.map((c) => ({
      ...c,
      align: c.align || getDefaultColumnAlign(c.id),
    }));
  };

  const buildPreviewRows = (data = {}) => {
    if (Array.isArray(data.rows) && data.rows.length) return data.rows;
    return [
      {
        id: 'row-1',
        startTime: '',
        duration: '',
        item: '',
        rowType: 'normal',
        rowColor: '',
        rowTextColor: '',
        cells: {},
      },
    ];
  };

  const buildHeaderGroups = (columns = []) => {
    const groups = [];
    let idx = 0;
    while (idx < columns.length) {
      const base = columns[idx];
      const baseLabel = (base.label || '').trim();
      let span = 1;
      idx += 1;
      while (idx < columns.length && (columns[idx].label || '').trim() === baseLabel) {
        span += 1;
        idx += 1;
      }
      groups.push({
        label: baseLabel,
        span,
        align: base.align || getDefaultColumnAlign(base.id),
      });
    }
    return groups;
  };

  const pickColumnWidths = (data = {}, columns = []) => {
    const raw = data.columnWidths && typeof data.columnWidths === 'object' ? data.columnWidths : {};
    const next = {};
    columns.forEach((c) => {
      const w = raw[c.id];
      if (typeof w === 'number' && Number.isFinite(w) && w > 0) {
        next[c.id] = w;
      }
    });
    return next;
  };

  const pickRowHeights = (data = {}, rows = []) => {
    const raw = data.rowHeights && typeof data.rowHeights === 'object' ? data.rowHeights : {};
    const next = {};
    rows.forEach((r) => {
      const h = raw[r.id];
      if (typeof h === 'number' && Number.isFinite(h) && h > 0) {
        next[r.id] = h;
      }
    });
    return next;
  };

  const findNowRowId = (table, adjustmentsMap) => {
    const ymd = normalizeTargetDate(table?.target_date);
    if (!ymd) return null;
    const data = table.data || {};
    const rows = applyRowAdjustments(buildPreviewRows(data), adjustmentsMap);
    const timeline = buildTimelineRows(rows, ymd);
    const nowMs = Date.now();
    let candidate = null;
    timeline.forEach((r) => {
      if (r.startMs <= nowMs && nowMs < r.endMs) {
        if (!candidate || candidate.startMs < r.startMs) {
          candidate = { id: r.id, startMs: r.startMs };
        }
      }
    });
    return candidate?.id || null;
  };

  const findNextStartMs = (table, adjustmentsMap) => {
    const ymd = normalizeTargetDate(table?.target_date);
    if (!ymd) return null;
    const data = table.data || {};
    const rows = applyRowAdjustments(buildPreviewRows(data), adjustmentsMap);
    const timeline = buildTimelineRows(rows, ymd);
    const nowMs = Date.now();
    let next = null;
    timeline.forEach((r) => {
      if (r.startMs > nowMs && (next === null || r.startMs < next)) {
        next = r.startMs;
      }
    });
    return next;
  };

  const openTimetableViewer = useCallback(
    (tableId) => {
      if (!eventId || !tableId) return;
      const url = `/artist/workspaces/event-operations/events/${encodeURIComponent(eventId)}/timetables/${encodeURIComponent(tableId)}`;
      openExternalUrl(url);
    },
    [eventId],
  );

  const fetchTimetables = async () => {
    if (!eventId) return;
    setTimetableLoading(true);
    try {
      // アーティスト用の共有専用エンドポイントを呼び出す
      const list = await listSharedEventTimetables(eventId);
      setTimetables(list);
      if (list.length && !activeTimetableId) {
        setActiveTimetableId(list[0].id);
      } else if (!list.find((t) => t.id === activeTimetableId)) {
        setActiveTimetableId(list[0]?.id || null);
      }
    } catch (err) {
      logUnexpectedSupplementalError('Artist shared timetables fetch failed:', err);
      setTimetables([]);
      setActiveTimetableId(null);
    } finally {
      setTimetableLoading(false);
    }
  };

  const fetchStageSeatingShared = async () => {
    if (!eventId) return;
    setStageSeatingSharedLoading(true);
    try {
      const res = await getArtistStageSeatingSharedSummary(eventId);
      setStageSeatingShared({
        layouts: Array.isArray(res?.layouts) ? res.layouts : [],
        shares: Array.isArray(res?.shares) ? res.shares : [],
      });
    } catch (err) {
      logUnexpectedSupplementalError('Shared stage seating fetch failed:', err);
      setStageSeatingShared({ layouts: [], shares: [] });
    } finally {
      setStageSeatingSharedLoading(false);
    }
  };

  const renderTimetable = (table) => {
    if (!table) return null;
    const data = table.data || {};
    const columns = buildPreviewColumns(data);
    const rows = applyRowAdjustments(buildPreviewRows(data), adjustmentsMap);
    const columnWidths = pickColumnWidths(data, columns);
    const rowHeights = pickRowHeights(data, rows);
    const headerGroups = buildHeaderGroups(columns);
    const announcements = Array.isArray(runtimeData?.announcements) ? runtimeData.announcements : [];
    const body = [];

    rows.forEach((r) => {
      const isNow = nowTimetableRowId === r.id;
      const memo = memoMap.get(String(r.id));
      const hasAdjustedTime =
        r.adjustedOffsetMinutes && r.adjustedStartTime && String(r.startTime || '').trim();
      const displayStartTime = hasAdjustedTime ? r.adjustedStartTime : r.startTime || '';

      body.push(
        <tr
          key={r.id}
          className={isNow ? 'is-now' : ''}
          style={rowHeights[r.id] ? { height: `${rowHeights[r.id]}px` } : undefined}
        >
          {columns.map((c) => {
            const cell = (r.cells && r.cells[c.id]) || {};
            if (cell.mergeParentId) return null;
            const spanProps = {
              rowSpan: cell.rowSpan || 1,
              colSpan: cell.colSpan || 1,
            };
            let value = '';
            if (c.id === 'startTime') {
              if (hasAdjustedTime) {
                const mode = r.adjustedOffsetMinutes > 0 ? 'is-push' : 'is-pull';
                value = (
                  <div className={`tt-time-adjusted ${mode}`}>
                    <span className="tt-time-original">{r.startTime || ''}</span>
                    <span className="tt-time-arrow">→</span>
                    <span className="tt-time-new">{displayStartTime}</span>
                  </div>
                );
              } else {
                value = displayStartTime || '';
              }
            } else if (c.id === 'duration') value = r.duration || '';
            else if (c.id === 'item') value = r.item || '';
            else value = cell.value || '';
            const bg = cell.color || r.rowColor || '';
            const txt = cell.textColor || r.rowTextColor || '';
            const align = c.align || getDefaultColumnAlign(c.id);
            return (
              <td
                key={`${r.id}-${c.id}`}
                {...spanProps}
                style={{
                  textAlign: align,
                  background: bg || undefined,
                  color: txt || undefined,
                  fontWeight: cell.bold ? 700 : undefined,
                  fontSize: cell.fontSize ? `${cell.fontSize}px` : undefined,
                  whiteSpace: 'pre-wrap',
                  verticalAlign: 'middle',
                  padding: '8px 10px',
                  borderBottom: '1px solid var(--slate-200)',
                }}
              >
                {value}
              </td>
            );
          })}
        </tr>,
      );

      if (memo?.content) {
        const memoVisibility = memo.visibility || 'all';
        body.push(
          <tr key={`${r.id}-memo`} className="timetable-row-memo">
            <td colSpan={columns.length} className="timetable-row-memo-cell">
              <div className="timetable-row-memo-content">
                <span className="timetable-row-memo-label">メモ</span>
                {memoVisibility === 'admin' ? (
                  <span className="timetable-row-memo-badge">管理者</span>
                ) : null}
                <span className="timetable-row-memo-text">{memo.content}</span>
              </div>
            </td>
          </tr>,
        );
      }
    });

    const colGroup =
      columns.length > 0 ? (
        <colgroup>
          {columns.map((c) => {
            const w = columnWidths[c.id];
            const width = typeof w === 'number' && w > 0 ? `${w}px` : null;
            return <col key={c.id} style={width ? { width, minWidth: width } : undefined} />;
          })}
        </colgroup>
      ) : null;

    return (
      <div className="artist-timetable-wrap">
        <div className="artist-timetable-card">
          {announcements.length ? (
            <div className="timetable-runtime-banner">
              {announcements.map((notice) => {
                const visibility = String(notice?.visibility || '').toLowerCase();
                const isAdminOnly = visibility === 'admin';
                return (
                  <div key={notice.id || notice.start_at} className="timetable-runtime-banner__item">
                    <div className="timetable-runtime-banner__header">
                      <span className="timetable-runtime-banner__title">お知らせ</span>
                      {isAdminOnly ? (
                        <span className="timetable-runtime-banner__badge">管理者のみ</span>
                      ) : null}
                    </div>
                    <div className="timetable-runtime-banner__content">{notice.content}</div>
                  </div>
                );
              })}
            </div>
          ) : null}
          <div className="artist-timetable-table-scroll">
            <table className="artist-timetable-table">
              {colGroup}
              <thead>
                <tr>
                  {headerGroups.map((g, idx) => (
                    <th key={`h-${idx}`} colSpan={g.span} style={{ textAlign: g.align || 'left' }}>
                      {g.label || ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>{body}</tbody>
          </table>
        </div>
      </div>
    </div>
    );
  };

  const fetchOriginalEventAssets = async (originalEventUuid) => {
    try {
      const originalEvent = await getEventDetail(originalEventUuid);
      if (!originalEvent) throw new Error('event not found');
      setFlyerUrls({
        front: getImageUrl(originalEvent.flyer_front_url),
        back: getImageUrl(originalEvent.flyer_back_url),
      });
      setBannerUrl(getBannerUrl(originalEvent.banner_image_url));
    } catch (error) {
      logUnexpectedSupplementalError('Original event assets fetch failed:', error);
      setFlyerUrls({ front: null, back: null });
      setBannerUrl(null);
      if (!isExpectedEmptySupplementalError(error)) {
        toastError('フライヤー/バナーの取得に失敗しました');
      }
    }
  };

  const fetchGenres = async () => {
    try {
      const data = await listGenres();
      const list = Array.isArray(data) ? data : Array.isArray(data?.genres) ? data.genres : [];
      setGenres(list);
    } catch (error) {
      console.error('Error fetching genres:', error);
      setGenres([]);
      toastError('ジャンル情報の取得に失敗しました');
    }
  };

  // ==== JST 今日判定 ====
  const jstTodayYmd = useMemo(() => {
    const f = new Intl.DateTimeFormat('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const parts = f.formatToParts(new Date());
    const y = parts.find((p) => p.type === 'year')?.value;
    const m = parts.find((p) => p.type === 'month')?.value;
    const d = parts.find((p) => p.type === 'day')?.value;
    return `${y}-${m}-${d}`.replace(/\//g, '-');
  }, []);

  const isToday = useMemo(() => {
    const ymd = String(eventData?.event_date || '').slice(0, 10);
    return ymd && ymd === jstTodayYmd;
  }, [eventData, jstTodayYmd]);
  const isEventCancelled = !!eventData?.canceled_at;
  const isConciergeEvent = Boolean(eventData?.is_concierge_event);

  // ==== 日付/時刻表示 ====
  const formatDate = (dateString) => formatDateJP(dateString) || '未設定';
  const formatTime = (timeString) => toHHmm(timeString) || '未設定';
  const formatEndAt = (isoOrLocal) => formatDateTimeJST(isoOrLocal) || '未設定';

  const formatAssignedAdminName = (admin) =>
    admin?.display_name ||
    admin?.full_name ||
    admin?.nickname ||
    admin?.username ||
    '未設定';

  const getParticipantImageStyle = (artist) => {
    if (artist.profile_picture) {
      const imageUrl = appendVariant(
        `${API_URL}${ARTIST_PROFILE_IMAGE_PATH}/${encodeURIComponent(artist.profile_picture)}`,
        'proxy'
      );
      return {
        backgroundImage: `url(${imageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    }
    const genderKey = String(artist.gender || artist.artist_gender || '').trim().toLowerCase();
    const bg =
      genderKey === 'male' || genderKey === 'man' || genderKey === 'm' || genderKey === '男性' || genderKey === '男'
        ? 'var(--person-avatar-artist-male-bg)'
        : genderKey === 'female' || genderKey === 'woman' || genderKey === 'f' || genderKey === '女性' || genderKey === '女'
        ? 'var(--person-avatar-artist-female-bg)'
        : 'var(--person-avatar-artist-unknown-bg)';
    return {
      backgroundColor: bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '13px',
      fontWeight: 600,
      color: '#fff',
    };
  };

  const assignedAdmins = useMemo(() => {
    if (!eventData || !Array.isArray(eventData.assigned_admins)) return [];
    const seen = new Set();
    return eventData.assigned_admins.filter((admin) => {
      if (!admin) return false;
      const rawKey =
        admin.admin_user_id ?? admin.id ?? admin.username ?? admin.display_name;
      if (rawKey === null || rawKey === undefined || rawKey === '') return true;
      const key = String(rawKey);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [eventData]);

  const assignedAdminsDisplay = assignedAdmins.length
    ? assignedAdmins.map((admin) => formatAssignedAdminName(admin)).join(' / ')
    : '未設定';

  const membersVisibleToArtists =
    eventData?.members_visible_to_artists === true ||
    eventData?.membersVisibleToArtists === true;

  const partsById = useMemo(() => {
    const map = new Map();
    (partsList || []).forEach((p) => {
      const idKey = toNum(p?.id ?? p?.value ?? p?.part_id);
      if (Number.isFinite(idKey)) map.set(idKey, p);
    });
    return map;
  }, [partsList]);

  const participantSections = useMemo(() => {
    const activeRows = (castingProgress || []).filter((row) =>
      PARTICIPANT_STATUSES.has(String(row.contract_status || row.status || '').toLowerCase())
    );
    const sortedCasting = (Array.isArray(castingInfo) ? castingInfo : [])
      .slice()
      .sort((a, b) => (a.sort_order ?? 9999) - (b.sort_order ?? 9999));
    const sections = sortedCasting.map((ci) => ({
      id: ci.id,
      label: ci.part,
      sortOrder: ci.sort_order ?? 9999,
      artists: [],
      isUnassigned: false,
    }));
    const sectionMap = new Map();
    sections.forEach((sec) => {
      sectionMap.set(sec.id, sec);
      sectionMap.set(String(sec.id), sec);
    });
    const undecided = [];

    const buildRow = (row, partLabel) => {
      const mainPartIds = parsePartIds(row.artist_parts);
      const mainPartId = mainPartIds[0] ?? null;
      const mainMeta = mainPartId != null ? partsById.get(toNum(mainPartId)) : null;
      const mainSort = mainMeta?.sort_order ?? 9999;
      return {
        artist_id: row.artist_id,
        name: row.artist_name || row.name || '',
        name_en: row.artist_name_en || row.name_en || '',
        profile_picture: row.artist_profile_picture || row.profile_picture || row.pic || '',
        gender: row.artist_gender || row.gender || '',
        is_chief: !!row.is_chief,
        is_concertmaster: !!row.is_concertmaster,
        custom_roles: Array.isArray(row.custom_roles)
          ? row.custom_roles
          : Array.isArray(row.customRoles)
          ? row.customRoles
          : [],
        instagram_url: row.instagram_url || '',
        twitter_url: row.twitter_url || '',
        facebook_url: row.facebook_url || '',
        hp_url: row.hp_url || '',
        profile_one_liner: row.profile_one_liner || '',
        partLabel,
        contractTs: toTimestamp(row.contract_updated_at || row.contractUpdatedAt),
        mainSort,
      };
    };

    activeRows.forEach((row) => {
      const section = sectionMap.get(row.casting_info_id);
      const partLabel = section?.label || 'パート未設定';
      const record = buildRow(row, partLabel);
      if (section) section.artists.push(record);
      else undecided.push(record);
    });

    const sortMembers = (list) => {
      list.sort((a, b) => {
        if (!!a.is_concertmaster !== !!b.is_concertmaster) {
          return a.is_concertmaster ? -1 : 1;
        }
        if (!!a.is_chief !== !!b.is_chief) return a.is_chief ? -1 : 1;
        const aTs = a.contractTs;
        const bTs = b.contractTs;
        if (aTs != null && bTs != null && aTs !== bTs) return aTs - bTs;
        if (aTs != null && bTs == null) return -1;
        if (aTs == null && bTs != null) return 1;
        return (a.name || '').localeCompare(b.name || '', 'ja');
      });
    };

    sections.forEach((sec) => sortMembers(sec.artists));
    undecided.sort((a, b) => {
      const aSort = Number.isFinite(a.mainSort) ? a.mainSort : 9999;
      const bSort = Number.isFinite(b.mainSort) ? b.mainSort : 9999;
      if (aSort !== bSort) return aSort - bSort;
      return (a.name || '').localeCompare(b.name || '', 'ja');
    });

    const decided = sections.filter((sec) => sec.artists.length > 0);
    if (undecided.length) {
      decided.push({
        id: 'unassigned',
        label: 'パート未設定',
        artists: undecided,
        isUnassigned: true,
      });
    }
    return decided;
  }, [castingInfo, castingProgress, partsById]);

  const participantBubbleArtists = useMemo(
    () => participantSections.flatMap((sec) => sec.artists),
    [participantSections]
  );

  const ticketSites = Array.isArray(eventData?.ticket_sites)
    ? eventData.ticket_sites
    : [];
  const tickets = Array.isArray(eventData?.tickets) ? eventData.tickets : [];
  const formatTicketPrice = (v) => {
    const num = Number(v);
    return Number.isFinite(num) ? `${num.toLocaleString()}円` : '—';
  };
  const formatTicketQuantity = (v) => {
    const num = Number(v);
    return Number.isFinite(num) ? `${num}枚` : '—';
  };
  const vendors = Array.isArray(eventData?.vendors) ? eventData.vendors : [];

  const renderTextBlock = (text) =>
    text ? (
      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{String(text)}</div>
    ) : null;

  const renderSelectedOptions = () =>
    eventData?.selected_options?.length ? (
      <div className="options-container">
        {eventData.selected_options.map((option, index) => (
          <span key={index} className="option-badge">
            {option}
          </span>
        ))}
      </div>
    ) : null;
  // ==== フライヤー ==== //
  const flyerOrder = useMemo(() => ['front', 'back'].filter((side) => flyerUrls[side]), [flyerUrls]);
  const flyerImages = useMemo(
    () => flyerOrder.map((side) => flyerUrls[side]),
    [flyerOrder, flyerUrls]
  );
  const hasMultipleFlyers = flyerImages.length > 1;
  const currentFlyerFaceIndex = flyerImages.length
    ? currentFlyerIndex % flyerImages.length
    : 0;
  const currentFlyerUrl = flyerImages.length
    ? flyerImages[currentFlyerFaceIndex]
    : null;

  const flyerToggleLabel = useMemo(() => {
    if (flyerImages.length <= 1) return '';
    if (flyerImages.length === 2) {
      return currentFlyerFaceIndex === 0 ? '裏面を見る' : '表面を見る';
    }
    return '次のフライヤー';
  }, [flyerImages.length, currentFlyerFaceIndex]);

  useEffect(() => {
    if (!flyerImages.length) {
      if (currentFlyerIndex !== 0) setCurrentFlyerIndex(0);
      return;
    }
    const normalized = currentFlyerIndex % flyerImages.length;
    if (normalized !== currentFlyerIndex) setCurrentFlyerIndex(normalized);
  }, [flyerImages.length, currentFlyerIndex]);

  const cycleFlyer = useCallback(
    (direction = 1) => {
      if (!flyerImages.length) return;
      setCurrentFlyerIndex((idx) => {
        const total = flyerImages.length;
        const next = ((idx + direction) % total + total) % total;
        return next;
      });
    },
    [flyerImages.length]
  );

  const openFlyerPreview = useCallback(
    (side) => {
      const idx = flyerOrder.indexOf(side);
      if (idx >= 0) setCurrentFlyerIndex(idx);
      if (flyerOrder.length) setShowFlyerPopup(true);
    },
    [flyerOrder]
  );

  const renderFlyerSection = () => (
    <div className="flyer-section">
      {flyerOrder.length === 0 ? (
        <div className="flyer-placeholder">
          <i className="fa-regular fa-image" aria-hidden="true"></i>
          <span>フライヤーは登録されていません</span>
        </div>
      ) : (
        <div className="flyer-stack">
          {flyerOrder.map((side) => {
            const label = side === 'front' ? '表面' : '裏面';
            return (
              <figure className="flyer-item" key={side}>
                <div className="flyer-slot">
                  <EventFlyerMedia
                    event={eventData}
                    imageUrl={flyerUrls[side]}
                    imageAlt={`${label}のフライヤー`}
                    sizeVariant={side === 'front' ? 'card' : 'large'}
                    borderRadius={12}
                    imageClassName="flyer-image"
                    onImageClick={() => openFlyerPreview(side)}
                  />
                </div>
                <figcaption className="flyer-item__caption">{label}</figcaption>
              </figure>
            );
          })}
        </div>
      )}
    </div>
  );

  const handleFlyerPopupInnerClick = useCallback((e) => {
    e.stopPropagation();
    const target = e.target;
    const isImage = target.closest?.('.expanded-flyer');
    const isToggle = target.closest?.('.flyer-popup-toggle');
    if (!isImage && !isToggle) {
      setShowFlyerPopup(false);
    }
  }, []);

  // ======================= ジャンル解決ロジック ======================= //
  const genreList = useMemo(() => (Array.isArray(genres) ? genres : []), [genres]);
  const genresById = useMemo(() => {
    const m = new Map();
    for (const g of genreList) m.set(Number(g.id), g);
    return m;
  }, [genreList]);

  const genresByValueLower = useMemo(() => {
    const m = new Map();
    for (const g of genreList) {
      if (g?.value) m.set(String(g.value).toLowerCase(), g);
    }
    return m;
  }, [genreList]);

  const parsePgIntArray = (s) => {
    const t = String(s || '').trim();
    if (t.startsWith('{') && t.endsWith('}')) {
      return t
        .slice(1, -1)
        .split(',')
        .map((n) => Number(n))
        .filter(Number.isInteger);
    }
    return null;
  };

  const coerceGenreToIds = (val) => {
    if (Array.isArray(val)) return val.map(Number).filter(Number.isInteger);
    if (typeof val === 'number') return Number.isInteger(val) ? [val] : [];
    if (typeof val === 'string') {
      const s = val.trim();
      if (!s) return [];
      if (s.startsWith('[')) {
        try {
          const arr = JSON.parse(s);
          if (Array.isArray(arr)) return arr.map(Number).filter(Number.isInteger);
        } catch {
          return [];
        }
        return [];
      }
      if (s.startsWith('{')) return parsePgIntArray(s) ?? [];
      // value 文字列で保存されていた旧データ
      const byValue = genresByValueLower.get(s.toLowerCase());
      if (byValue) return [Number(byValue.id)];
      const n = Number(s);
      return Number.isInteger(n) ? [n] : [];
    }
    return [];
  };

  const buildGenreChips = useCallback((rawGenre, custom) => {
    const ids = coerceGenreToIds(rawGenre);
    if (ids.length === 0) {
      const label = typeof rawGenre === 'string' ? rawGenre : '';
      return label
        ? [
            {
              id: 'legacy',
              label,
              bg: GENRE_CHIP_FALLBACK_BG,
              fg: GENRE_CHIP_FALLBACK_TEXT,
            },
          ]
        : [];
    }
    return ids
      .map((id) => {
        const g = genresById.get(Number(id));
        if (!g) return null;
        const isOther = String(g.value) === 'other';
        const label =
          isOther && (custom || '').trim() ? custom.trim() : g.label || '';
        const bg = g.color || g.label_color || g.bg_color || GENRE_CHIP_FALLBACK_BG;
        const fg = g.text_color || GENRE_CHIP_FALLBACK_TEXT;
        return { id, label, bg, fg, value: g.value };
      })
      .filter(Boolean);
  }, [coerceGenreToIds, genresById]);

  const buildGroupFlyerSrc = useCallback((event) => {
    if (!event?.flyer_front_url) return null;
    return appendVariant(getImageUrl(event.flyer_front_url), 'card');
  }, []);

  const resolveGroupVenueDisplay = useCallback((event) => {
    const venueId = event?.venue_id ?? null;
    const venueName = event?.venue || '';
    return {
      name: venueName || '',
      link: venueId ? `/artist/workspaces/event-operations/venues/${venueId}` : null,
    };
  }, []);

  const handleGroupFlyerLoad = useCallback((eventUuid, target) => {
    if (!eventUuid || !target) return;
    const { naturalWidth, naturalHeight } = target;
    if (!naturalWidth || !naturalHeight) return;
    const isLandscape = naturalWidth > naturalHeight;
    setGroupFlyerLandscapeMap((prev) => {
      if (prev[eventUuid] === isLandscape) return prev;
      return { ...prev, [eventUuid]: isLandscape };
    });
  }, []);

  const isTodayDate = useCallback(
    (rawDate) => {
      const ymd = String(rawDate || '').slice(0, 10);
      return !!ymd && ymd === jstTodayYmd;
    },
    [jstTodayYmd]
  );

  const groupEventRows = useMemo(
    () =>
      (Array.isArray(groupEvents) ? groupEvents : []).filter(
        (event) => event && event.event_uuid
      ),
    [groupEvents]
  );

  const shouldShowGroupEventsPanel = hasGroupEvents && groupEventRows.length > 0;

  const renderGroupGenreBadges = useCallback(
    (event) => {
      const chips = buildGenreChips(
        event?.genre,
        event?.custom_genre || event?.customGenre || ''
      );
      const isConciergeEvent = Boolean(event?.is_concierge_event || event?.concierge_application_id);
      const hasAnyBadge =
        chips.length > 0 || isConciergeEvent || event?.is_current_event;
      if (!hasAnyBadge) return null;

      return (
        <div className="event-list-row__badges" aria-label="イベント属性">
          {event?.is_current_event ? (
            <span className="genre event-list-row__badge group-role-current">表示中</span>
          ) : null}
          {isConciergeEvent ? (
            <span
              className="genre event-list-row__badge"
              style={{ backgroundColor: 'var(--purple-600)', color: '#fff' }}
            >
              コンシェルジュ
            </span>
          ) : null}
          {chips.map((chip, index) => (
            <span
              key={`${event?.event_uuid || 'event'}-group-genre-${chip.id || index}`}
              className="genre event-list-row__badge"
              style={{
                backgroundColor: chip?.bg || 'var(--indigo-500)',
                color: chip?.fg || '#fff',
              }}
            >
              {chip?.label || 'ジャンル'}
            </span>
          ))}
        </div>
      );
    },
    [buildGenreChips]
  );

  const renderGroupEventRow = useCallback(
    (event) => {
      const venueDisplay = resolveGroupVenueDisplay(event);
      const venueForRow =
        venueDisplay.name || venueDisplay.link
          ? { ...venueDisplay, name: venueDisplay.name || '会場未設定' }
          : null;
      return (
        <EventListRow
          key={event.event_uuid}
          event={event}
          imgSrc={buildGroupFlyerSrc(event)}
          imgAlt={event.name ? `${event.name} のフライヤー` : 'フライヤー'}
          isLandscape={!!groupFlyerLandscapeMap[event.event_uuid]}
          onFlyerLoad={(img) => handleGroupFlyerLoad(event.event_uuid, img)}
          onClick={() => navigate(`/artist/workspaces/event-operations/events/${event.event_uuid}`)}
          isToday={isTodayDate(event.event_date)}
          todayLabel="本日"
          isCancelled={!!event?.canceled_at}
          venueDisplay={venueForRow}
          badges={renderGroupGenreBadges(event)}
          progressItems={[]}
        />
      );
    },
    [
      buildGroupFlyerSrc,
      groupFlyerLandscapeMap,
      handleGroupFlyerLoad,
      isTodayDate,
      navigate,
      renderGroupGenreBadges,
      resolveGroupVenueDisplay,
    ]
  );

  const infoPublishAtLabel = useMemo(
    () => (eventData?.info_publish_at ? formatDateTimeJST(eventData.info_publish_at) : ''),
    [eventData?.info_publish_at]
  );

  // eventData 未取得時のローディング表示
  if (!eventData) {
    return (
      <div className="artist-dashboard">
        <ArtistSidebar />
        <div className="event-detail-page artist-event-detail-page">
          <div className="event-detail-container artist-event-detail-container">
            <DetailPageSkeleton variant="event" sectionCount={3} />
          </div>
        </div>
      </div>
    );
  }

  const genreChips = buildGenreChips(
    eventData.genre,
    eventData.custom_genre || eventData.customGenre
  );

  const titleText = eventData?.name || 'イベント名未設定';
  const subTitleText = (() => {
    const raw = (eventData?.performance_type && String(eventData.performance_type).trim()) || '';
    if (raw) return raw;
    const flagKey = eventData?.performance_flag || '';
    return flagKey ? performanceFlagLabels[flagKey] || '' : '';
  })();
  const headerEventDateLabel = (() => {
    if (!eventData?.event_date) return '';
    const formatted = formatDate(eventData.event_date);
    if (!formatted || formatted === '未設定') return '';
    return `開催日: ${formatted}`;
  })();
  const isInfoPublished = eventData?.info_is_published === true;

  return (
    <div className="artist-dashboard">
      <ArtistSidebar />
      {/* 外側ラッパ：スクロール領域（シェルは管理者と共通の event-detail-page/EventDetailCommon.css） */}
      <div className="event-detail-page artist-event-detail-page">
        {/* 内側カード本体 */}
        <div
          className={`event-detail-container artist-event-detail-container${
            isToday ? ' is-today' : ''
          }`}
        >
          <div className="edc-title-row">
            <div className="title-stack">
              <div className="title-date-row">
                {headerEventDateLabel ? (
                  <p className="event-date-label" aria-label="開催日">
                    {headerEventDateLabel}
                  </p>
                ) : null}
                {isConciergeEvent ? (
                  <span className="concierge-badge" aria-label="コンシェルジュイベント">
                    コンシェルジュ
                  </span>
                ) : null}
                {isToday && (
                  <span className="today-badge" aria-label="本日開催">
                    本日
                  </span>
                )}
              </div>
              <h2 className="page-title page-title--left">{titleText}</h2>
              {subTitleText ? (
                <p className="page-subtitle">{subTitleText}</p>
              ) : null}
            </div>
          </div>

          {!isInfoPublished && (
            <div className="event-public-status-banner" role="status" aria-live="polite">
              <div className="event-public-status-icon" aria-hidden="true">
                <i className="fa-solid fa-earth-asia" aria-hidden="true"></i>
              </div>
              <div className="event-public-status-text">
                <div className="event-public-status-head">
                  <span className="event-public-status-badge">外部未公開</span>
                  <span>サイト上ではまだ公開されていません。</span>
                </div>
                <div className="event-public-status-sub">
                  外部未公開のため情報の取り扱いに注意してください。
                </div>
                {infoPublishAtLabel ? (
                  <div className="event-public-status-sub event-public-status-sub--schedule">
                    公開予約: {infoPublishAtLabel}
                  </div>
                ) : null}
              </div>
            </div>
          )}

          {/* 中止情報（アナウンスのみ） */}
          {isEventCancelled && (
            <div className="event-cancelled-stack">
              <div className="event-cancelled-banner" role="status" aria-live="polite">
                <div className="event-cancelled-icon" aria-hidden="true">
                  <i className="fa-solid fa-ban" aria-hidden="true"></i>
                </div>
                <div className="event-cancelled-text">
                  <div className="event-cancelled-head">
                    <span className="event-cancelled-badge">中止</span>
                    <span>
                      このイベントは中止されています（{formatDateTimeJST(eventData.canceled_at)} 中止）。
                    </span>
                  </div>
                  {(eventData.cancel_reason || eventData.cancelReason) ? (
                    <div className="event-cancelled-reason">
                      理由: {eventData.cancel_reason || eventData.cancelReason}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          )}

          <div className="event-detail-top-layout">
          <div className="event-detail-columns event-panel event-overview-panel">
            {flyerUrls.front || flyerUrls.back ? (
              <div className="flyer-panel">
                {renderFlyerSection()}
              </div>
            ) : null}

            <section className="info-panel">
              <div className="panel-heading">
                <div>
                  <h3>イベント情報</h3>
                </div>
              </div>
              <div className="event-info-container">
                <table className="event-info-table">
                  <tbody>
                    <tr>
                      <th>ジャンル</th>
                      <td>
                        <div className="genre-chip-wrap">
                          {genreChips.length ? (
                            genreChips.map((c) => (
                              <span
                                key={c.id}
                                className="artist-event-genre-badge colored"
                                style={{
                                  background: c.bg,
                                  color: c.fg,
                                  borderColor: c.fg,
                                }}
                                title={c.value}
                              >
                                {c.label}
                              </span>
                            ))
                          ) : (
                            <span>-</span>
                          )}
                        </div>
                      </td>
                    </tr>
                    {getEventPerformanceStructureRows(eventData).map((row) => (
                      <tr key={row.label}>
                        <th>{row.label}</th>
                        <td>{row.value}</td>
                      </tr>
                    ))}
                      {isMultiPerformanceEventData ? (
                        <tr>
                          <th>公演情報</th>
                          <td>
                            <div className="multi-performance-detail-list">
                              {multiPerformanceRows.map((performance, index) => {
                                const venue = getPerformanceVenueInfo(performance);
                                return (
                                  <div
                                    key={performance?.id || performance?.sort_order || index}
                                    className="multi-performance-detail-item"
                                  >
                                    <strong>{getPerformanceName(performance, `公演${index + 1}`)}</strong>
                                    <span>
                                      {getPerformanceDate(performance)?.replace(/-/g, '/') || '日付未設定'}
                                      {' '}
                                      {[
                                        performance.open_time ? `${formatTime(performance.open_time)} 開場` : '',
                                        performance.start_time ? `${formatTime(performance.start_time)} 開演` : '',
                                        performance.end_time ? `${formatTime(performance.end_time)} 終演` : '',
                                      ].filter(Boolean).join(' / ')}
                                    </span>
                                    <span className="multi-performance-detail-venue">
                                      <EventVenueDisplay
                                        venueName={venue.name}
                                        venueId={venue.venueId}
                                        venueDetailBasePath="/artist/workspaces/event-operations/venues"
                                        address={venue.address}
                                        mapUrl={venue.mapUrl}
                                        fallback="会場未設定"
                                        linkClassName="side-link"
                                      />
                                    </span>
                                    {venue.address ? (
                                      <span className="multi-performance-detail-address">{venue.address}</span>
                                    ) : null}
                                    {performance.notes ? <span>{performance.notes}</span> : null}
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      ) : (
                        <>
                          <tr>
                            <th>日付</th>
                            <td>{formatDate(eventData.event_date)}</td>
                          </tr>
                          <tr>
                            <th>開場</th>
                            <td>{formatTime(eventData.open_time)}</td>
                          </tr>
                          <tr>
                            <th>開演</th>
                            <td>{formatTime(eventData.start_time)}</td>
                          </tr>
                          <tr>
                            <th>イベント終了日時</th>
                            <td>{formatEndAt(eventData.event_end_at)}</td>
                          </tr>
                          <tr>
                            <th>会場名</th>
                            <td>
                              <EventVenueDisplay
                                venueName={venueDisplayName}
                                venueId={venueIdValue}
                                venueDetailBasePath="/artist/workspaces/event-operations/venues"
                                address={venueResolvedAddress}
                                mapUrl={mapLinkUrl}
                                fallback="未設定"
                                className="event-venue-cell"
                                linkClassName="side-link"
                              />
                            </td>
                          </tr>
                          <tr>
                            <th>{(venueLink || venueResolvedAddress) ? '住所' : '開催都道府県'}</th>
                            <td className="event-venue-cell">
                              {venueResolvedAddress || eventData.prefecture || '未設定'}
                              {(venueResolvedAddress || eventData.prefecture) && (
                                <button
                                  type="button"
                                  className="copy-action"
                                  onClick={() => navigator.clipboard?.writeText(venueResolvedAddress || eventData.prefecture || '')}
                                  aria-label="住所をコピー"
                                  title="住所をコピー"
                                >
                                  <i className="fa-regular fa-clipboard" aria-hidden="true"></i>
                                </button>
                              )}
                            </td>
                          </tr>
                        </>
                      )}

                    {eventData.additional_dates?.map((date, index) => (
                      <tr key={index}>
                        <th>追加日程</th>
                        <td>
                          {formatDate(date.additional_date)}：
                          {date.additional_date_title}
                          {date.description && ` — ${date.description}`}
                        </td>
                      </tr>
                    ))}

                    <tr>
                      <th>出演者</th>
                      <td>
                        {eventData.casts?.map((cast, index) => {
                          const displayName = (cast.artist_id ? (cast.artist_name || cast.cast_name) : cast.cast_name) || '';
                          const roleLabel = (cast.cast_role || '').trim();
                          return (
                            <div key={cast?.id || index}>
                              {roleLabel ? `${roleLabel}: ` : ''}
                              {cast.artist_id ? (
                                <button
                                  type="button"
                                  className="cast-artist-action"
                                  onClick={() => openArtistProfile(cast)}
                                >
                                  {displayName.trim() || '（未設定）'}
                                </button>
                              ) : (
                                displayName.trim() || '（未設定）'
                              )}
                            </div>
                          );
                        })}
                      </td>
                    </tr>

                    <tr>
                      <th>イベント概要</th>
                      <td>{renderTextBlock(eventData.event_overview)}</td>
                    </tr>

                    <tr>
                      <th>プログラム</th>
                      <td>{renderTextBlock(eventData.program)}</td>
                    </tr>

                    {tickets.length > 0 && (
                      <tr>
                        <th>チケット種別</th>
                        <td className="ticket-site-cell">
                          {tickets.map((t, index) => (
                            <div key={`${t.ticket_type || index}-${index}`} className="ticket-site-line">
                              <span className="ticket-site-name">{t.ticket_type || `種別${index + 1}`}</span>
                              <span className="ticket-site-note">{formatTicketPrice(t.price)}</span>
                              <span className="ticket-site-note">{formatTicketQuantity(t.quantity)}</span>
                            </div>
                          ))}
                        </td>
                      </tr>
                    )}

                    <tr>
                      <th>料金・チケット情報</th>
                      <td>{renderTextBlock(eventData.ticket_info)}</td>
                    </tr>

                    {ticketSites.length > 0 && (
                      <tr>
                        <th>チケットサイト</th>
                        <td className="ticket-site-cell">
                          {ticketSites.map((site, index) => (
                            <div
                              key={`${site.site_url || index}-${index}`}
                              className="ticket-site-line"
                            >
                              {site.site_name ? (
                                <span className="ticket-site-name">
                                  {site.site_name}
                                </span>
                              ) : null}
                              {site.site_url ? (
                                <a
                                  href={site.site_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="ticket-site-link"
                                >
                                  {site.site_url}
                                </a>
                              ) : null}
                              {!site.site_name && !site.site_url ? (
                                <span>—</span>
                              ) : null}
                            </div>
                          ))}
                        </td>
                      </tr>
                    )}

                    {vendors.length > 0 && (
                      <tr>
                        <th>業者情報</th>
                        <td className="ticket-site-cell">
                          {vendors.map((v, index) => (
                            <div
                              key={`${v.vendor_name || index}-${index}`}
                              className="ticket-site-line"
                            >
                              {v.vendor_name ? (
                                <span className="ticket-site-name">
                                  {v.vendor_name}
                                </span>
                              ) : null}
                              {v.request_note ? (
                                <span className="ticket-site-note">
                                  {v.request_note}
                                </span>
                              ) : null}
                              {v.memo ? (
                                <span
                                  className="ticket-site-note"
                                  style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                >
                                  {String(v.memo)}
                                </span>
                              ) : null}
                              {!v.vendor_name &&
                              !v.request_note &&
                              !v.memo ? (
                                <span>—</span>
                              ) : null}
                            </div>
                          ))}
                        </td>
                      </tr>
                    )}

                    <tr>
                      <th>オプション</th>
                      <td>{renderSelectedOptions()}</td>
                    </tr>

                    <tr>
                      <th>主催</th>
                      <td>{eventData.organizer}</td>
                    </tr>
                    <tr>
                      <th>運営</th>
                      <td>{eventData.operator}</td>
                    </tr>
                    <tr>
                      <th>担当者</th>
                      <td>{assignedAdminsDisplay}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* フライヤー拡大ポップアップ */}
            {showFlyerPopup && (
              <div
                className="flyer-popup"
                onClick={() => setShowFlyerPopup(false)}
              >
                <div
                  className="flyer-popup-inner"
                  onClick={handleFlyerPopupInnerClick}
                >
                  {currentFlyerUrl && (
                    <img
                      src={currentFlyerUrl}
                      alt="拡大フライヤー"
                      className="expanded-flyer"
                    />
                  )}
                  {hasMultipleFlyers && (
                    <button
                      type="button"
                      className="flyer-popup-toggle"
                      onClick={() => cycleFlyer(1)}
                    >
                      {flyerToggleLabel || '次を見る'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
          {/* /event-detail-columns */}

          <ArtistSharedTicketSalesPanel eventUuid={eventId} />

          {bannerUrl ? (
            <section className="event-panel banner-panel">
              <div className="panel-heading">
                <div>
                  <h3>イベントバナー</h3>
                </div>
              </div>
              <div className="banner-frame">
                <img src={bannerUrl} alt="イベントバナー" className="banner-image" />
              </div>
            </section>
          ) : null}

          {shouldShowGroupEventsPanel ? (
            <section className="event-panel group-events-panel">
              <div className="panel-heading">
                <div>
                  <h3>グループイベント</h3>
                </div>
                <div className="group-events-panel__summary">
                  <span className="group-events-panel__summary-item">
                    表示対象: {groupEventRows.length}件
                  </span>
                </div>
              </div>
              <div className="event-list-container group-events-list-container">
                {groupEventsLoading ? (
                  <EventListSkeleton rows={3} layout="row" />
                ) : (
                  <div className="event-list-rows">
                    {groupEventRows.map((event) => renderGroupEventRow(event))}
                  </div>
                )}
              </div>
            </section>
          ) : null}

          <section className="event-panel participants-panel">
            <div className="panel-heading">
              <div>
                <h3>参加アーティスト</h3>
              </div>
              <div className="panel-actions participants-panel__actions">
                {membersVisibleToArtists && (
                  <div className="segmented participants-view-toggle" role="group" aria-label="表示形式">
                    <button
                      type="button"
                      className={`participants-view-btn${participantView === 'list' ? ' is-active' : ''}`}
                      onClick={() => setParticipantView('list')}
                      aria-label="リスト表示"
                      title="リスト表示"
                    >
                      <i className="fa-solid fa-list" aria-hidden="true"></i>
                    </button>
                    <button
                      type="button"
                      className={`participants-view-btn${participantView === 'bubble' ? ' is-active' : ''}`}
                      onClick={() => setParticipantView('bubble')}
                      aria-label="バブル表示"
                      title="バブル表示"
                    >
                      <i className="fa-solid fa-spinner" aria-hidden="true"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="participants-body">
              {!membersVisibleToArtists ? (
                <div className="participants-empty">参加メンバー決定後公開</div>
              ) : participantSections.length === 0 ? (
                <div className="participants-empty">参加アーティストがいません。</div>
              ) : participantView === 'list' ? (
                <div className="participants-list">
                  {participantSections.map((section) => (
                    <div className="participants-group" key={section.id}>
                      <div className="participants-group__title">{section.label}</div>
                      <div className="participants-group__list">
                        {section.artists.map((artist) => (
                          <button
                            key={artist.artist_id}
                            type="button"
                            className="participant-row"
                            onClick={() => setProfileModalArtist(buildParticipantProfile(artist))}
                          >
                            <div className="participant-identity">
                              <div className="participant-avatar" style={getParticipantImageStyle(artist)}>
                                {!artist.profile_picture && (artist.name || '').charAt(0)}
                              </div>
                              <div className="participant-main">
                                <div className="participant-badges">
                                  {artist.is_concertmaster ? (
                                    <span className="participant-concertmaster-badge">
                                      コンサートマスター
                                    </span>
                                  ) : null}
                                  {artist.is_chief ? (
                                    <span className="participant-chief-badge">首席</span>
                                  ) : null}
                                  {Array.isArray(artist.custom_roles) && artist.custom_roles.length
                                    ? artist.custom_roles.map((role) => (
                                        <span
                                          key={`${role?.id || role?.name || 'role'}-badge`}
                                          className="participant-role-badge"
                                        >
                                          {role?.name || role?.role_name || '役職'}
                                        </span>
                                      ))
                                    : null}
                                </div>
                                <div className="participant-name">
                                  <span>{artist.name || '—'}</span>
                                </div>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <ArtistBubbleView
                  artists={participantBubbleArtists}
                  showInlineCard
                  cardVariant="profile"
                  getSizeBoost={(artist) => {
                    if (artist?.is_concertmaster) return 2;
                    if (artist?.is_chief) return 1.5;
                    return 0;
                  }}
                />
              )}
            </div>
          </section>
          </div>

          <section className="event-panel materials-panel">
            <EventMaterialExplorer
              eventUuid={eventId}
              role="artist"
              title="公開資料"
              canView
              canDownload
              canUpload
              canManage={false}
              emptyLabel="共有資料はまだありません"
            />
          </section>

          {(timetableLoading || timetables.length > 0) && (
            <section className="event-panel artist-timetable-panel">
              <div className="panel-heading">
                <div>
                  <h3>タイムテーブル</h3>
                </div>
              </div>
              <div className="artist-timetable-body">
                {timetableLoading ? (
                  <ActionRowsSkeleton
                    rows={3}
                    containerClassName="artist-timetable-tabs"
                    itemClassName="timetable-tab-simple"
                    mainClassName="tab-main"
                  />
                ) : timetables.length === 0 ? (
                  <p className="muted">共有されたタイムテーブルはまだありません。</p>
                ) : (
                  <>
                    <div className="artist-timetable-tabs">
                      {timetables.map((t) => (
                        <button
                          key={t.id}
                          className={`timetable-tab-simple ${activeTimetableId === t.id ? 'is-active' : ''}`}
                          onClick={() => setActiveTimetableId(t.id)}
                        >
                          <span className="tab-main">
                            <span className="tab-title">{t.title || '無題'}</span>
                            <span className="tab-date">{t.target_date ? formatDateJPFromAny(t.target_date) : '日付未設定'}</span>
                          </span>
                          <span
                            className="tab-open-link"
                            role="button"
                            tabIndex={0}
                            aria-label="タイムテーブルを別タブで開く"
                            title="タイムテーブルを別タブで開く"
                            onClick={(e) => {
                              e.stopPropagation();
                              openTimetableViewer(t.id);
                            }}
                            onKeyDown={(e) => {
                              if (e.key !== 'Enter' && e.key !== ' ') return;
                              e.preventDefault();
                              e.stopPropagation();
                              openTimetableViewer(t.id);
                            }}
                          >
                            <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i>
                          </span>
                        </button>
                      ))}
                    </div>
                    {renderTimetable(timetables.find((t) => t.id === activeTimetableId) || timetables[0])}
                  </>
                )}
              </div>
            </section>
          )}

          {(stageSeatingSharedLoading || stageSeatingShared.layouts.length > 0 || stageSeatingShared.shares.length > 0) && (
            <section className="event-panel artist-stage-seating-panel" id="stage-seating">
              <div className="panel-heading">
                <div>
                  <h3>共有シーティング</h3>
                </div>
                {canEditSharedStageSeating ? (
                  <DsButton
                    type="button"
                    variant="secondary"
                    onClick={() => navigate(`/artist/workspaces/event-operations/events/${eventId}/stage-seating`)}
                  >
                    作成ページ
                  </DsButton>
                ) : (
                  <DsButton
                    type="button"
                    variant="secondary"
                    onClick={() => navigate(`/artist/workspaces/event-operations/events/${eventId}/stage-seating/preview`)}
                  >
                    プレビュー
                  </DsButton>
                )}
              </div>
              <div className="artist-stage-seating-body">
                {stageSeatingSharedLoading ? (
                  <ActionRowsSkeleton
                    rows={3}
                    containerClassName="artist-stage-seating-list"
                    itemClassName="artist-stage-seating-item"
                    mainClassName="artist-stage-seating-main"
                    actionsClassName="artist-stage-seating-badges"
                  />
                ) : stageSeatingShared.layouts.length === 0 ? (
                  <button
                    type="button"
                    className="artist-stage-seating-empty-link"
                    onClick={() => navigate(`/artist/workspaces/event-operations/events/${eventId}/stage-seating/preview`)}
                  >
                    共有されたシーティングをプレビューで開く
                    <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
                  </button>
                ) : (
                  <div className="artist-stage-seating-list">
                    {stageSeatingShared.layouts.map((layout) => (
                      <button
                        key={layout.id}
                        type="button"
                        className="artist-stage-seating-item"
                        onClick={() => navigate(`/artist/workspaces/event-operations/events/${eventId}/stage-seating/preview`)}
                      >
                        <span>
                          <strong>{layout.title || '無題'}</strong>
                          <small>{layout.performance_name || layout.group_label || 'イベント全体'}</small>
                        </span>
                        <span className="artist-stage-seating-badges">
                          {layout.can_edit ? <em>編集可</em> : null}
                          <em>{Array.isArray(layout.placements) ? layout.placements.length : 0}人</em>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </section>
          )}

          <div className="page-back-row">
            <DsButton variant="ghost" onClick={() => navigate(-1)}>
              ← 戻る
            </DsButton>
          </div>
        </div>
        {/* /artist-event-detail-container */}
      </div>
      {/* /artist-event-detail-page */}
      {profileModalArtist ? (
        <ArtistMemberProfileModal
          type="artist"
          artist={profileModalArtist}
          onClose={() => setProfileModalArtist(null)}
          allowArtistLink={false}
        />
      ) : null}
    </div>
  );
}
